/**
 * Trusted Time Module
 *
 * Provides tamper-resistant time using:
 * 1. Cloudflare as primary trusted time source (on SW startup)
 * 2. API Date header as fallback (only if Cloudflare fails)
 * 3. Monotonic clock (performance.now()) for elapsed time measurement
 * 4. Drift detection to handle device suspension (performance.now() freezes during sleep)
 *
 * All state is in memory - resets when SW sleeps.
 * On each SW wake, initTrustedTime() syncs from Cloudflare.
 */

const TRUSTED_TIME_URL = 'https://1.1.1.1/cdn-cgi/trace';
const DRIFT_THRESHOLD_MS = 10_000;
const RESYNC_TIMEOUT_MS = 8_000;
const RESYNC_COOLDOWN_MS = 30_000;
const RETRY_DELAY_MS = 2_000;
const MAX_RETRIES = 7;

const delay = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

export const createTrustedTime = () => {
  let serverTimeAtSync: number | undefined;
  let perfNowAtSync: number | undefined;
  let dateNowAtSync: number | undefined;
  let resyncPromise: Promise<void> | undefined;
  let lastResyncAttemptAt: number | undefined;

  const syncFromServerTime = (serverTime: number) => {
    serverTimeAtSync = serverTime;
    perfNowAtSync = performance.now();
    dateNowAtSync = Date.now();
  };

  /**
   * Attempts to fetch time from Cloudflare with retries.
   * Retries only on network errors; server/parse errors abort immediately.
   */
  const fetchCloudflareWithRetry = async (): Promise<number | undefined> => {
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(TRUSTED_TIME_URL, { cache: 'no-store' });
        if (!response.ok) return undefined;

        const text = await response.text();
        const match = /ts=(\d+\.?\d*)/.exec(text);
        if (!match) return undefined;

        return Math.floor(parseFloat(match[1]) * 1000);
      } catch {
        if (attempt < MAX_RETRIES) await delay(RETRY_DELAY_MS);
      }
    }
    return undefined;
  };

  /**
   * Performs resync from Cloudflare.
   * Deduplicates concurrent calls and respects cooldown.
   */
  const doResync = (): Promise<void> => {
    if (resyncPromise) return resyncPromise;

    const inCooldown =
      lastResyncAttemptAt !== undefined &&
      performance.now() - lastResyncAttemptAt < RESYNC_COOLDOWN_MS;
    if (inCooldown) return Promise.resolve();

    lastResyncAttemptAt = performance.now();

    resyncPromise = fetchCloudflareWithRetry()
      .then(serverTime => {
        if (serverTime !== undefined) {
          console.log(
            `[TrustedTime] Synced: ${new Date(serverTime).toISOString()}`
          );
          syncFromServerTime(serverTime);
        }
      })
      .finally(() => {
        resyncPromise = undefined;
      });

    return resyncPromise;
  };

  /**
   * Detects drift between performance.now() (freezes during sleep) and Date.now().
   */
  const hasDrift = (): boolean => {
    if (perfNowAtSync === undefined || dateNowAtSync === undefined)
      return false;
    const deltaPerf = performance.now() - perfNowAtSync;
    const deltaDate = Date.now() - dateNowAtSync;
    return Math.abs(deltaDate - deltaPerf) > DRIFT_THRESHOLD_MS;
  };

  const hasSyncState = () =>
    serverTimeAtSync !== undefined && perfNowAtSync !== undefined;

  const computeTrustedNow = (): number | undefined => {
    if (serverTimeAtSync === undefined || perfNowAtSync === undefined) {
      return undefined;
    }
    return Math.floor(serverTimeAtSync + (performance.now() - perfNowAtSync));
  };

  return {
    /**
     * Syncs trusted time from an HTTP Date header.
     *
     * Used as fallback when API responses include a Date header,
     * allowing time sync even if Cloudflare is unavailable.
     *
     * @param dateHeader - The Date header value from an HTTP response
     */
    syncTimeFromHeader(dateHeader: string | null) {
      if (!dateHeader) {
        console.warn('[TrustedTime] Missing Date header');
        return;
      }
      const serverTime = Date.parse(dateHeader);
      if (Number.isNaN(serverTime)) {
        console.warn(`[TrustedTime] Invalid Date header: ${dateHeader}`);
        return;
      }
      syncFromServerTime(serverTime);
    },

    /**
     * Initializes trusted time by syncing from Cloudflare.
     *
     * Should be called on Service Worker startup. Uses fire-and-forget pattern
     * to avoid blocking initialization when Cloudflare is unavailable.
     *
     * @returns Promise that resolves when sync completes (or fails)
     */
    initTrustedTime: () => doResync(),

    /**
     * Checks if trusted time is currently synchronized and reliable.
     *
     * If drift is detected (device was suspended), triggers a background resync
     * and returns false. This is a self-healing mechanism - callers should
     * retry later or use fallback time.
     *
     * @returns true if trusted time is synced and ready to use
     */
    isTrustedTimeSynced(): boolean {
      if (!hasSyncState() || resyncPromise) return false;
      if (hasDrift()) {
        doResync();
        return false;
      }
      return true;
    },

    /**
     * Returns current timestamp immediately using trusted time.
     * If drift is detected, triggers resync in background and returns stale time.
     * Use this for time-critical paths where blocking is not acceptable.
     *
     * @returns Timestamp in ms, or undefined if not synced
     */
    getTrustedNowImmediate(): number | undefined {
      if (!hasSyncState()) return undefined;
      if (hasDrift()) doResync();
      return computeTrustedNow();
    },

    /**
     * Returns current timestamp using trusted time.
     * If drift is detected, waits for resync before returning.
     * Use this when accurate time is required and blocking is acceptable.
     *
     * @returns Timestamp in ms, or undefined if not synced or resync fails/times out
     */
    async getTrustedNowInMs(): Promise<number | undefined> {
      if (!hasSyncState()) return undefined;
      if (!hasDrift() && !resyncPromise) return computeTrustedNow();

      // Drift detected or resync in progress - wait for resync
      const resyncToWait = doResync();

      const timedOut = await Promise.race([
        resyncToWait.then(() => false),
        delay(RESYNC_TIMEOUT_MS).then(() => true),
      ]);

      if (timedOut || !hasSyncState() || hasDrift()) return undefined;
      return computeTrustedNow();
    },
  };
};

export const trustedTime = createTrustedTime();
